const collectionProductsCache = new Map()

async function fetchCollectionProducts (handle, basePath) {
  if (collectionProductsCache.has(handle)) {
    return collectionProductsCache.get(handle)
  }

  const products = []
  let page = 1

  while (true) {
    const res = await fetch(`${basePath}collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`)
    if (!res.ok) break
    const data = await res.json()
    const batch = data.products || []
    products.push(...batch)
    if (batch.length < 250) break
    page++
  }

  collectionProductsCache.set(handle, products)
  return products
}

function matchCollectionProducts (products, term) {
  const q = term.toLowerCase().trim()
  if (!q) return []

  const terms = q.split(/\s+/).filter(Boolean)

  return products.filter((product) => {
    const haystack = [
      product.title,
      product.product_type,
      product.vendor,
      product.handle,
      ...(product.tags || []),
      ...((product.variants || []).map((variant) => variant.title)),
    ].filter(Boolean).join(' ').toLowerCase()

    return terms.every((token) => haystack.includes(token))
  })
}

function formatProductPrice (product) {
  const min = parseFloat(product.price_min || product.variants?.[0]?.price || 0)
  const max = parseFloat(product.price_max || min)
  if (!min) return ''

  const format = (amount) => {
    if (window.FinanceUtils?.formatMoneyAmount) {
      return window.FinanceUtils.formatMoneyAmount(amount)
    }
    return `£${amount.toFixed(2)}`
  }

  if (max > min) {
    return `<span>${format(min)}</span> <span aria-hidden="true">-</span> <span>${format(max)}</span>`
  }

  return `<span>${format(min)}</span>`
}

function productToSearchItem (product, basePath) {
  const imageSource = product.featured_image?.src || product.images?.[0]?.src || ''
  const image = imageSource
    ? `<img src="${imageSource}" alt="" width="100%" height="100%" class="rounded object-cover aspect-[4/3] w-14 h-auto shrink-0" loading="lazy">`
    : ''

  return {
    href: `${basePath}products/${product.handle}`,
    title: product.title,
    priceHtml: formatProductPrice(product),
    image,
  }
}

class MenuSearch extends HTMLElement {
  constructor () {
    super()
    this.cachedItems = []
    this.inactivityTimer = null
    this.suppressBlurClose = false
    this.resultsShown = false
    this.clearVisible = false
  }

  connectedCallback () {
    this.input = this.querySelector('input[type="search"]')
    this.label = this.querySelector('label')
    this.wrapper = this.querySelector('.mobile-menu__search-suggestions-wrapper')
    this.list = this.querySelector('ul[role="listbox"]')
    this.clearButton = this.querySelector('[data-search-clear]')
    this.noResultsText = this.getAttribute('data-no-results-text') || 'No results found'

    if (!this.input || !this.list || !this.wrapper) return

    // Create debounced handler after elements are available
    this.debouncedHandleQuery = this.debounce((val) => this.handleQuery(val), 250)

    this.setupEvents()
  }

  setupEvents () {
    this.input.addEventListener('focus', () => this.onFocus())
    this.input.addEventListener('blur', () => this.onBlur())
    this.input.addEventListener('input', () => {
      this.updateClearButtonVisibility()
      this.debouncedHandleQuery(this.input.value)
      this.startInactivityTimer()
    })

    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.onClear())
    }

    this.outsideClickHandler = (evt) => {
      if (!this.contains(evt.target)) {
        this.hideSuggestions()
        this.resultsShown = false
      }
    }
    document.addEventListener('click', this.outsideClickHandler, true)
    document.addEventListener('touchend', this.outsideClickHandler, true)

    this.wrapper.addEventListener('scroll', () => this.updateListFader())
  }

  disconnectedCallback () {
    document.removeEventListener('click', this.outsideClickHandler, true)
    document.removeEventListener('touchend', this.outsideClickHandler, true)
  }

  onFocus () {
    this.activateLabel()
    this.updateClearButtonVisibility()
    if (this.input.value.trim().length >= 2) {
      if (this.cachedItems.length) {
        this.renderResults(this.cachedItems)
      } else {
        // If we have value but no cached items, maybe we should fetch?
        // But usually handleQuery handles it.
        // If user focuses back, and we have valid query but no cache?
        // We should probably trigger search or just show nothing until input?
        // Original code showed results if cached.
        // If not cached, it probably relies on typing?
        // Let's match original logic: if cached, render.
        // But DO NOT call showSuggestions unconditionally.
      }
    }
    this.startInactivityTimer()
    this.scrollToView()
  }

  onBlur () {
    setTimeout(() => {
      if (!this.suppressBlurClose && !this.input.value.trim()) {
        this.deactivateLabel()
        this.setClearButtonVisibility(false)
      }
    }, 100)
  }

  onClear () {
    this.input.value = ''
    this.list.innerHTML = ''
    this.cachedItems = []
    this.hideSuggestions()
    this.resultsShown = false
    this.updateClearButtonVisibility()
    this.deactivateLabel()
    this.input.focus()
  }

  scrollToView () {
    try {
      const container = this.closest('.mobile-menu__level')
      if (container) {
        const cRect = container.getBoundingClientRect()
        const bRect = this.getBoundingClientRect()
        let stickyOffset = 0
        const stickyBack = container.querySelector('.mobile-menu__back')
        if (stickyBack) {
          const sbRect = stickyBack.getBoundingClientRect()
          stickyOffset = (sbRect.height || 60) + 5
        }
        const delta = bRect.top - cRect.top - stickyOffset - 8
        const targetTop = Math.max(0, container.scrollTop + delta)
        container.scrollTo({ top: targetTop, behavior: 'smooth' })
      }
    } catch (_) {}
  }

  activateLabel () {
    if (!this.label) return
    try {
      this.label.classList.remove('top-1/2', '-translate-y-1/2', 'text-base')
      this.label.classList.add('top-2', 'text-sm')
      const ph = this.input.getAttribute('data-placeholder') || this.label.textContent.trim()
      this.input.setAttribute('data-placeholder', ph)
      this.input.placeholder = ph
    } catch (_) {}
  }

  deactivateLabel () {
    if (!this.label) return
    if (this.input.value.trim() !== '') return
    try {
      this.label.classList.remove('top-2', 'text-sm')
      this.label.classList.add('top-1/2', '-translate-y-1/2')
      this.input.placeholder = ''
    } catch (_) {}
  }

  hideSuggestions () {
    this.wrapper.classList.add('hidden')
  }

  showSuggestions () {
    this.wrapper.classList.remove('hidden')
  }

  debounce (fn, wait) {
    let t
    return (...args) => {
      clearTimeout(t)
      t = setTimeout(() => fn.apply(this, args), wait)
    }
  }

  ensureListFader () {
    let hasItems = false
    for (const child of Array.from(this.list.children || [])) {
      if (!child.classList.contains('mobile-menu__search-fader')) { hasItems = true; break }
    }
    if (!hasItems) {
      const existing = this.wrapper.querySelector('.mobile-menu__search-fader')
      if (existing) existing.remove()
      return null
    }
    let fader = this.wrapper.querySelector('.mobile-menu__search-fader')
    if (!fader) {
      fader = document.createElement('div')
      fader.className = 'mobile-menu__search-fader pointer-events-none sticky bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent z-10 transition-opacity duration-150 opacity-0 block w-full'
      fader.setAttribute('aria-hidden', 'true')
      const srSpan = document.createElement('span')
      srSpan.className = 'sr-only'
      srSpan.textContent = 'Scroll down to see more'
      fader.appendChild(srSpan)
      this.wrapper.appendChild(fader)
    }
    return fader
  }

  updateListFader () {
    const f = this.ensureListFader()
    if (!f) {
      // Remove negative margin when no fader is present
      this.list.classList.remove('-mb-12')
      return
    }
    const sh = this.wrapper.scrollHeight || 0
    const ch = this.wrapper.clientHeight || 0
    const st = this.wrapper.scrollTop || 0

    // Compute scrollability EXCLUDING the fader height so the fader doesn't create fake overflow
    const fh = f.offsetHeight || 0
    const contentScrollHeight = Math.max(0, sh - fh)
    const can = contentScrollHeight > ch + 1
    const atEnd = st + ch >= contentScrollHeight - 1

    // If there is nothing to scroll, remove the fader completely
    if (!can) {
      f.remove()
      this.list.classList.remove('-mb-12')
      return
    }

    // Otherwise, show/hide based on scroll position and ensure negative margin is present
    this.list.classList.add('-mb-12')
    f.classList.remove('hidden')
    if (!atEnd) {
      f.classList.remove('opacity-0')
      f.classList.add('opacity-100')
    } else {
      f.classList.remove('opacity-100')
      f.classList.add('opacity-0')
    }
  }

  setClearButtonVisibility (visible) {
    if (!this.clearButton || this.clearVisible === visible) return
    this.clearVisible = visible
    if (visible) {
      this.clearButton.classList.remove('hidden')
      this.clearButton.classList.add('flex')
    } else {
      this.clearButton.classList.add('hidden')
      this.clearButton.classList.remove('flex')
    }
  }

  updateClearButtonVisibility () {
    if (!this.clearButton) return
    const hasValue = this.input.value && this.input.value.trim().length > 0
    this.setClearButtonVisibility(hasValue)

    if (hasValue) {
      this.input.classList.add('desktop-menu__search-input--has-value')
    } else {
      this.input.classList.remove('desktop-menu__search-input--has-value')
    }
  }

  renderResults (items) {
    this.list.innerHTML = ''
    this.wrapper.removeAttribute('style')
    // Remove any existing fader before rendering
    const existingFader = this.wrapper.querySelector('.mobile-menu__search-fader')
    if (existingFader) existingFader.remove()

    if (!items.length) {
      const li = document.createElement('li')
      li.className = 'px-3.5 py-2.5 text-gray-500 cursor-default'
      li.textContent = this.noResultsText
      this.list.appendChild(li)
      this.list.classList.remove('-mb-12')
      this.showSuggestions()
      this.resultsShown = true
      return
    }

    items.slice(0, 6).forEach((item) => {
      const li = document.createElement('li')
      li.className = 'border-b border-clay last:border-b-0'
      const a = document.createElement('a')
      a.href = item.href
      a.className = 'block w-full'
      a.setAttribute('tabindex', '-1')

      const row = document.createElement('div')
      row.className = 'flex items-center gap-4 py-3 px-3 hover:bg-oat focus:outline-hidden'

      if (item.image) {
        const imgWrap = document.createElement('div')
        imgWrap.className = 'rounded bg-oat aspect-[4/3] w-16 h-auto overflow-hidden flex items-center justify-center'
        imgWrap.innerHTML = item.image
        row.appendChild(imgWrap)
      } else {
        const ph = document.createElement('div')
        ph.className = 'rounded bg-oat aspect-[4/3] w-16 h-auto flex items-center justify-center'
        const sr = document.createElement('span')
        sr.className = 'sr-only'
        sr.textContent = 'No image available'
        ph.appendChild(sr)
        row.appendChild(ph)
      }

      const content = document.createElement('div')
      content.className = 'flex-1 min-w-0'
      const title = document.createElement('div')
      title.className = 'text-base font-medium truncate'
      title.textContent = item.title
      content.appendChild(title)
      if (item.priceHtml) {
        const price = document.createElement('div')
        price.className = 'text-sm'
        price.innerHTML = item.priceHtml
        content.appendChild(price)
      }
      row.appendChild(content)
      a.appendChild(row)
      li.appendChild(a)
      this.list.appendChild(li)
    })

    // Show before measuring heights
    this.showSuggestions()

    this.resultsShown = true
    this.updateListFader()
  }

  parsePredictiveHtml (htmlText) {
    const doc = new DOMParser().parseFromString(htmlText, 'text/html')
    const container = doc.querySelector('#shopify-section-predictive-search')
    if (!container) return []
    const anchors = container.querySelectorAll('.predictive-search__result-item a')
    const items = []
    anchors.forEach(a => {
      const href = a.getAttribute('href') || '#'
      const titleEl = a.querySelector('.predictive-search__result-title')
      const priceEl = a.querySelector('.predictive-search__result-price')
      const imgEl = a.querySelector('img')
      items.push({
        href,
        title: titleEl ? titleEl.textContent.trim() : a.textContent.trim(),
        priceHtml: priceEl ? priceEl.innerHTML : '',
        image: imgEl ? imgEl.outerHTML : '',
      })
    })
    return items
  }

  async fetchGlobalResults (term, basePath) {
    const url = `${basePath}search/suggest?q=${encodeURIComponent(term)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search`
    const res = await fetch(url)
    if (!res.ok) return []
    const text = await res.text()
    return this.parsePredictiveHtml(text)
  }

  async fetchCollectionScopedResults (term, handle, basePath) {
    const products = await fetchCollectionProducts(handle, basePath)
    return matchCollectionProducts(products, term)
      .slice(0, 6)
      .map((product) => productToSearchItem(product, basePath))
  }

  async fetchResults (term) {
    const basePath = (window?.Shopify?.routes?.root || '/')
    const collectionHandle = this.getAttribute('data-collection-handle')

    if (collectionHandle) {
      return this.fetchCollectionScopedResults(term, collectionHandle, basePath)
    }

    return this.fetchGlobalResults(term, basePath)
  }

  async handleQuery (val) {
    const q = (val || '').trim()
    if (q.length < 2) {
      this.hideSuggestions()
      this.resultsShown = false
      return
    }
    try {
      this.list.innerHTML = ''
      const loading = document.createElement('li')
      loading.className = 'px-3.5 py-2.5 flex items-center justify-center'
      loading.innerHTML = '<div class="h-5 w-5 text-black animate-spin" role="status" aria-live="polite"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg><span class="sr-only">Loading...</span></div>'
      this.list.appendChild(loading)
      this.showSuggestions()
      const items = await this.fetchResults(q)
      this.cachedItems = items
      this.renderResults(items)
    } catch (e) {
      this.hideSuggestions()
      this.resultsShown = false
    }
  }

  startInactivityTimer () {
    clearTimeout(this.inactivityTimer)
    if (!this.resultsShown) return
    this.inactivityTimer = setTimeout(() => {
      try {
        this.suppressBlurClose = true
        this.input.setAttribute('readonly', 'true')
        this.input.blur()
        setTimeout(() => {
          this.input.removeAttribute('readonly')
          this.suppressBlurClose = false
        }, 180)
      } catch (_) {
        this.suppressBlurClose = false
      }
    }, 2000)
  }
}

if (!customElements.get('menu-search')) {
  customElements.define('menu-search', MenuSearch)
}
